import React, { useEffect } from "react";
import { Modal, Form, Input, Upload, Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { openNotificationError } from "../../../utils/Utils";
import {useImageLoader} from "../../customHook/ImageLoader";

export default function StickerPackModal({ open, onClose, onSubmit, editingPack }) {
    const [form] = Form.useForm();

    const { imageUrl } = useImageLoader(editingPack?.thumbnailResourceId);

    useEffect(() => {
        if (editingPack) {
            form.setFieldsValue({
                name: editingPack.name,
                description: editingPack.description,
                fileList: editingPack.thumbnailResourceId
                    ? [
                        {
                            uid: "-1",
                            name: "Current thumbnail",
                            status: "done",
                            url: imageUrl,
                        },
                    ]
                    : [],
            });
        } else {
            form.resetFields();
        }
    }, [editingPack, imageUrl, form]);

    const handleBeforeUpload = (file) => {
        const isImage = file.type.startsWith("image/");
        if (!isImage) {
            openNotificationError("You can only upload image files (png, jpg, jpeg, gif, webp...)");
            return Upload.LIST_IGNORE;
        }

        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            openNotificationError("Image must be smaller than 5MB!");
            return Upload.LIST_IGNORE;
        }

        return false;
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();

            const file = values.fileList?.[0];
            const hasNewFile = file && !file.url;

            const payload = {
                name: values.name,
                description: values.description,
            };

            if (hasNewFile) {
                payload.fileName = file.name;
                payload.base64 = file.thumbUrl;
            }

            onSubmit(payload, editingPack?.packId);
        } catch (err) {
            console.warn("Validation failed:", err);
        }
    };

    return (
        <Modal
            title={editingPack ? "Edit Sticker Pack" : "Create Sticker Pack"}
            open={open}
            onOk={handleOk}
            onCancel={onClose}
            okText={editingPack ? "Update" : "Create"}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="name"
                    label="Pack Name"
                    rules={[
                        { required: true, message: "Pack name is required" },
                        { max: 64, message: "Name cannot exceed 64 characters" },
                    ]}
                >
                    <Input placeholder="Enter sticker pack name" />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Description"
                    rules={[
                        { max: 256, message: "Description cannot exceed 256 characters" },
                    ]}
                >
                    <Input.TextArea rows={2} placeholder="Enter description (optional)" />
                </Form.Item>

                <Form.Item
                    name="fileList"
                    label="Thumbnail"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => e?.fileList}
                >
                    <Upload
                        maxCount={1}
                        listType="picture"
                        beforeUpload={handleBeforeUpload}
                    >
                        <Button icon={<UploadOutlined />}>Select Image</Button>
                    </Upload>
                </Form.Item>
            </Form>
        </Modal>
    );
}
